/**
 * EliteProductionDashboard.jsx
 * 
 * cost tracking, resource management, and performance monitoring, etc...
 * 
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Container, Row, Col, Card, Button, ButtonGroup, Badge, Table, Dropdown } from 'react-bootstrap';
import { 
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import axios from 'axios';
import { appendProductionInstanceScope } from '../productionRequestScope';

import DashboardIcon from '@mui/icons-material/Dashboard';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PercentIcon from '@mui/icons-material/Percent';
import InventoryIcon from '@mui/icons-material/Inventory';
import SpeedIcon from '@mui/icons-material/Speed';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PendingIcon from '@mui/icons-material/Pending';
import FactoryIcon from '@mui/icons-material/Factory';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import StopIcon from '@mui/icons-material/Stop';
import BuildIcon from '@mui/icons-material/Build';
import TimelineIcon from '@mui/icons-material/Timeline';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AddIcon from '@mui/icons-material/Add';
import LoopIcon from '@mui/icons-material/Loop';
const scopedProductionUrl = (input) => appendProductionInstanceScope(input, {
  pathname: window.location.pathname, apiBase: process.env.REACT_APP_NODEJS_SERVER_URL,
});


const THEMES = {
  light: {
    background: '#F8F9FA',
    cardBg: '#FFFFFF',
    surface: '#F9FAFB',
    border: '#E5E7EB',
    textPrimary: '#111827',
    textSecondary: '#6B7280',
    textMuted: '#9CA3AF',
    success: '#10B981',
    warning: '#F59E0B',
    danger: '#EF4444',
    info: '#3B82F6',
    primary: '#6366F1',
    hover: '#F3F4F6'
  },
  dark: {
    background: '#111827',
    cardBg: '#1F2937',
    surface: '#374151',
    border: '#4B5563',
    textPrimary: '#F9FAFB',
    textSecondary: '#D1D5DB',
    textMuted: '#9CA3AF',
    success: '#10B981',
    warning: '#F59E0B',
    danger: '#EF4444',
    info: '#3B82F6',
    primary: '#6366F1',
    hover: '#4B5563'
  }
};

const STATUS_COLORS = {
  Planned: '#6366F1',
  Scheduled: '#3B82F6',
  InProduction: '#10B981',
  QC: '#F59E0B',
  Completed: '#22C55E',
  OnHold: '#EF4444'
};


const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value || 0);
};

const formatPercent = (value) => `${(value || 0).toFixed(1)}%`;

const formatDate = (date) => {
  if (!date) return 'N/A';
  return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const normalizeStatus = (status) => {
  if (!status) return 'Planned';
  return status.replace(/_/g, '');
};



const KPICard = ({ icon: Icon, label, value, subtitle, trend, trendUp, loading, colors }) => (
  <Card style={{
    background: colors.cardBg,
    border: `1px solid ${colors.border}`,
    borderRadius: 12,
    height: '100%',
    transition: 'all 0.2s',
    cursor: 'default'
  }}
  onMouseEnter={(e) => {
    e.currentTarget.style.transform = 'translateY(-4px)';
    e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.1)';
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.transform = 'translateY(0)';
    e.currentTarget.style.boxShadow = 'none';
  }}
  >
    <Card.Body className="p-4">
      {loading ? (
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <LoopIcon className="spinner" style={{ fontSize: 32, color: colors.textMuted }} />
        </div>
      ) : (
        <>
          <div className="d-flex align-items-start justify-content-between mb-3">
            <div style={{
              width: 48,
              height: 48,
              borderRadius: 12,
              background: `${colors.primary}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Icon style={{ fontSize: 24, color: colors.primary }} />
            </div>
            {trend && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                fontSize: 13,
                fontWeight: 700,
                color: trendUp ? colors.success : colors.danger,
                background: trendUp ? `${colors.success}15` : `${colors.danger}15`,
                padding: '4px 10px',
                borderRadius: 6
              }}>
                {trendUp ? <TrendingUpIcon style={{ fontSize: 16 }} /> : <TrendingDownIcon style={{ fontSize: 16 }} />}
                {trend}
              </div>
            )}
          </div>
          
          <div style={{ fontSize: 12, color: colors.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
            {label}
          </div>
          
          <div style={{ fontSize: 32, fontWeight: 700, color: colors.textPrimary, lineHeight: 1.2, marginBottom: 6 }}>
            {value}
          </div>
          
          {subtitle && (
            <div style={{ fontSize: 12, color: colors.textSecondary, fontWeight: 500 }}>
              {subtitle}
            </div>
          )}
        </>
      )}
    </Card.Body>
  </Card>
);


const DepartmentCard = ({ dept, colors }) => {
  const utilizationColor = dept.utilization > 80 ? colors.success : dept.utilization > 50 ? colors.warning : colors.danger;
  
  return (
    <Card style={{
      background: colors.cardBg,
      border: `1px solid ${colors.border}`,
      borderRadius: 10,
      height: '100%',
      transition: 'all 0.2s'
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.background = colors.hover;
      e.currentTarget.style.borderColor = colors.primary;
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = colors.cardBg;
      e.currentTarget.style.borderColor = colors.border;
    }}
    >
      <Card.Body className="p-3">
        <div className="d-flex align-items-center gap-2 mb-3">
          <FactoryIcon style={{ fontSize: 20, color: colors.primary }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: colors.textPrimary }}>
              {dept.name}
            </div>
            <div style={{ fontSize: 11, color: colors.textSecondary }}>
              {dept.activeJobs} active jobs
            </div>
          </div>
          <Badge bg="secondary" style={{ fontSize: 10, fontWeight: 600 }}>
            {dept.machines} machines
          </Badge>
        </div>

        <div className="mb-2">
          <div className="d-flex justify-content-between mb-1">
            <span style={{ fontSize: 11, color: colors.textSecondary, fontWeight: 600 }}>Utilization</span>
            <span style={{ fontSize: 11, color: utilizationColor, fontWeight: 700 }}>{dept.utilization}%</span>
          </div>
          <div style={{
            height: 8,
            background: colors.surface,
            borderRadius: 4,
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${dept.utilization}%`,
              height: '100%',
              background: utilizationColor,
              transition: 'width 0.5s ease'
            }} />
          </div>
        </div>

        <div className="d-flex justify-content-between" style={{ fontSize: 11, color: colors.textSecondary }}>
          <span>Revenue: {formatCurrency(dept.revenue)}</span>
          <span>Margin: {formatPercent(dept.margin)}</span>
        </div>
      </Card.Body>
    </Card>
  );
};

const MachineStatusChip = ({ machine, colors }) => {
  const statusConfig = {
    Running: { icon: PlayArrowIcon, color: colors.success, bg: `${colors.success}15` },
    Idle: { icon: PauseIcon, color: colors.warning, bg: `${colors.warning}15` },
    Down: { icon: ErrorOutlineIcon, color: colors.danger, bg: `${colors.danger}15` },
    Maintenance: { icon: BuildIcon, color: colors.info, bg: `${colors.info}15` }
  };

  const config = statusConfig[machine.status] || statusConfig.Idle;
  const Icon = config.icon;

  return (
    <div style={{
      background: colors.cardBg,
      border: `1px solid ${colors.border}`,
      borderLeft: `3px solid ${config.color}`,
      borderRadius: 8,
      padding: '10px 12px',
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      transition: 'all 0.2s'
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.background = colors.hover;
      e.currentTarget.style.borderLeftWidth = '4px';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = colors.cardBg;
      e.currentTarget.style.borderLeftWidth = '3px';
    }}
    >
      <div style={{
        width: 32,
        height: 32,
        borderRadius: 8,
        background: config.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
      }}>
        <Icon style={{ fontSize: 16, color: config.color }} />
      </div>
      
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ 
          fontSize: 13, 
          fontWeight: 600, 
          color: colors.textPrimary,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          {machine.name}
        </div>
        <div style={{ fontSize: 11, color: colors.textSecondary }}>
          {machine.status} • {machine.utilization}%
        </div>
      </div>
    </div>
  );
};


const AlertItem = ({ alert, colors }) => {
  const typeConfig = {
    danger: { icon: ErrorOutlineIcon, color: colors.danger, bg: `${colors.danger}15` },
    warning: { icon: WarningAmberIcon, color: colors.warning, bg: `${colors.warning}15` },
    info: { icon: CheckCircleIcon, color: colors.info, bg: `${colors.info}15` }
  };

  const config = typeConfig[alert.type] || typeConfig.info;
  const Icon = config.icon;

  return (
    <div style={{
      background: colors.surface,
      border: `1px solid ${colors.border}`,
      borderLeft: `3px solid ${config.color}`,
      borderRadius: 8,
      padding: 12,
      marginBottom: 10,
      transition: 'all 0.2s'
    }}
    onMouseEnter={(e) => e.currentTarget.style.background = colors.hover}
    onMouseLeave={(e) => e.currentTarget.style.background = colors.surface}
    >
      <div className="d-flex gap-2">
        <Icon style={{ fontSize: 18, color: config.color, flexShrink: 0, marginTop: 2 }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: colors.textPrimary, marginBottom: 4 }}>
            {alert.title}
          </div>
          <div style={{ fontSize: 11, color: colors.textSecondary, lineHeight: 1.5 }}>
            {alert.message}
          </div>
        </div>
      </div>
    </div>
  );
};


const EliteProductionDashboard = ({ department }) => {
  // Theme
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('dashboard-theme');
    return saved === 'dark';
  });
  const colors = isDark ? THEMES.dark : THEMES.light;

  // Time period
  const [timePeriod, setTimePeriod] = useState('week'); // day, week, month

  // Data states
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stepsData, setStepsData] = useState([]);
  const [machinesData, setMachinesData] = useState([]);
  const [costData, setCostData] = useState({});



  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {

      let stepsUrl = `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/steps`;
      if (department && department !== 'production') {
        stepsUrl += `?department=${encodeURIComponent(department)}`;
      }

      const stepsRes = await axios.get(scopedProductionUrl(stepsUrl), {
        headers: { accessToken: localStorage.getItem('accessToken') }
      });

      if (stepsRes.data.success) {
        setStepsData(stepsRes.data.data);
        
     
        await enrichWithCostData(stepsRes.data.data);
      }


      const machinesRes = await axios.get(
        scopedProductionUrl(`${process.env.REACT_APP_NODEJS_SERVER_URL}/production/machines`),
        { headers: { accessToken: localStorage.getItem('accessToken') } }
      );

      if (machinesRes.data.success) {
        setMachinesData(machinesRes.data.data);
      }

    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [department]);

  const enrichWithCostData = async (steps) => {
    const uniqueKeys = [...new Set(steps.map(s => s.external_key))].filter(Boolean);
    
    const costPromises = uniqueKeys.slice(0, 20).map(key => // Limit to 20 for performance
      axios.get(scopedProductionUrl(`${process.env.REACT_APP_NODEJS_SERVER_URL}/production/order-details`), {
        params: { external_key: key },
        headers: { accessToken: localStorage.getItem('accessToken') }
      }).catch(() => null)
    );

    const costResponses = await Promise.all(costPromises);
    
    const costMap = {};
    costResponses.forEach((res, idx) => {
      if (res?.data?.success) {
        const key = uniqueKeys[idx];
        const { charges, meta } = res.data;
        
        const step = steps.find(s => s.external_key === key);
        const qty = step?.qtyRequired || 1;
        
        const deptName = meta.eligible_departments?.[0]?.name || step?.department;
        const deptCharges = charges.byDepartment?.[deptName] || [];
        const charge = deptCharges[0] || { net: 0, retail: 0 };
        
        costMap[key] = {
          cost: charge.net * qty,
          revenue: charge.retail * qty,
          profit: (charge.retail - charge.net) * qty,
          margin: charge.retail > 0 ? ((charge.retail - charge.net) / charge.retail * 100) : 0
        };
      }
    });

    setCostData(costMap);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllData();
    setTimeout(() => setRefreshing(false), 500);
  };

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);



  const stats = useMemo(() => {
    const totalRevenue = Object.values(costData).reduce((sum, c) => sum + (c.revenue || 0), 0);
    const totalCost = Object.values(costData).reduce((sum, c) => sum + (c.cost || 0), 0);
    const totalProfit = totalRevenue - totalCost;
    const avgMargin = totalRevenue > 0 ? (totalProfit / totalRevenue * 100) : 0;

    const activeJobs = stepsData.filter(s => ['Scheduled', 'In_Production'].includes(s.status)).length;
    
    const runningMachines = machinesData.filter(m => m.status === 'Running').length;
    const totalMachines = machinesData.length;
    const avgUtilization = totalMachines > 0 
      ? machinesData.reduce((sum, m) => sum + (m.utilization || 0), 0) / totalMachines 
      : 0;

    return {
      totalRevenue,
      totalCost,
      totalProfit,
      avgMargin,
      activeJobs,
      totalJobs: stepsData.length,
      runningMachines,
      totalMachines,
      avgUtilization
    };
  }, [stepsData, machinesData, costData]);

  // Orders by Status (for pie chart)
  const ordersByStatus = useMemo(() => {
    const statusCounts = {};
    stepsData.forEach(step => {
      const status = normalizeStatus(step.status);
      statusCounts[status] = (statusCounts[status] || 0) + 1;
    });

    return Object.entries(statusCounts).map(([status, count]) => ({
      name: status,
      value: count,
      color: STATUS_COLORS[status] || '#6B7280'
    }));
  }, [stepsData]);


  const departmentPerformance = useMemo(() => {
    const deptMap = {};

    stepsData.forEach(step => {
      const dept = step.department || 'Unknown';
      if (!deptMap[dept]) {
        deptMap[dept] = {
          name: dept,
          jobs: 0,
          revenue: 0,
          profit: 0,
          machines: 0,
          utilization: 0
        };
      }

      deptMap[dept].jobs++;
      
      const cost = costData[step.external_key];
      if (cost) {
        deptMap[dept].revenue += cost.revenue || 0;
        deptMap[dept].profit += cost.profit || 0;
      }
    });


    machinesData.forEach(machine => {
      const dept = machine.department || 'Unknown';
      if (deptMap[dept]) {
        deptMap[dept].machines++;
        deptMap[dept].utilization += machine.utilization || 0;
      }
    });


    Object.values(deptMap).forEach(dept => {
      if (dept.machines > 0) {
        dept.utilization = Math.round(dept.utilization / dept.machines);
      }
      dept.margin = dept.revenue > 0 ? ((dept.profit / dept.revenue) * 100) : 0;
    });

    return Object.values(deptMap).sort((a, b) => b.revenue - a.revenue);
  }, [stepsData, machinesData, costData]);

  // Recent orders (last 8)
  const recentOrders = useMemo(() => {
    return stepsData
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .slice(0, 8)
      .map(step => ({
        ...step,
        cost: costData[step.external_key] || {}
      }));
  }, [stepsData, costData]);

  // Alerts
  const alerts = useMemo(() => {
    const alertList = [];

    // Overdue jobs
    const overdue = stepsData.filter(s => {
      if (!s.plannedFinish) return false;
      return new Date(s.plannedFinish) < new Date() && !s.isFullyCompleted;
    });
    if (overdue.length > 0) {
      alertList.push({
        type: 'danger',
        title: 'Overdue Jobs',
        message: `${overdue.length} jobs past their deadline`
      });
    }

    // Low margin jobs
    const lowMargin = Object.entries(costData).filter(([_, c]) => c.margin > 0 && c.margin < 15);
    if (lowMargin.length > 0) {
      alertList.push({
        type: 'warning',
        title: 'Low Margin Alert',
        message: `${lowMargin.length} jobs with margins below 15%`
      });
    }

    // Idle machines
    const idle = machinesData.filter(m => m.status === 'Idle' && m.utilization < 30);
    if (idle.length > 0) {
      alertList.push({
        type: 'info',
        title: 'Idle Capacity',
        message: `${idle.length} machines underutilized`
      });
    }

    // Down machines
    const down = machinesData.filter(m => m.status === 'Down');
    if (down.length > 0) {
      alertList.push({
        type: 'danger',
        title: 'Equipment Down',
        message: `${down.length} machines offline`
      });
    }

    return alertList;
  }, [stepsData, machinesData, costData]);

  // Machine status summary
  const machineStatusSummary = useMemo(() => {
    return machinesData.slice(0, 6); // Show top 6 machines
  }, [machinesData]);

  // Toggle theme
  const toggleTheme = () => {
    const newTheme = !isDark;
    setIsDark(newTheme);
    localStorage.setItem('dashboard-theme', newTheme ? 'dark' : 'light');
  };



  return (
    <div style={{ 
      background: colors.background, 
      minHeight: '100vh',
      padding: '24px'
    }}>
      <Container fluid>

        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 style={{ 
              margin: 0, 
              fontSize: 28, 
              fontWeight: 700, 
              color: colors.textPrimary,
              display: 'flex',
              alignItems: 'center',
              gap: 12
            }}>
              <DashboardIcon style={{ fontSize: 32, color: colors.primary }} />
              Production Dashboard
              <Badge bg="primary" style={{ fontSize: 11, fontWeight: 600, padding: '4px 10px' }}>
                LIVE
              </Badge>
            </h2>
            <p style={{ margin: '6px 0 0 0', color: colors.textSecondary, fontSize: 14 }}>
              {department && department !== 'production' 
                ? `${department.charAt(0).toUpperCase() + department.slice(1)} Department`
                : 'All Departments'} · Real-time production insights
            </p>
          </div>

          <div className="d-flex align-items-center gap-2">
            <ButtonGroup size="sm">
              {['day', 'week', 'month'].map(period => (
                <Button
                  key={period}
                  variant={timePeriod === period ? 'primary' : 'outline-secondary'}
                  onClick={() => setTimePeriod(period)}
                  style={{ fontSize: 12, fontWeight: 600, textTransform: 'capitalize' }}
                >
                  {period === 'day' ? 'Today' : `This ${period}`}
                </Button>
              ))}
            </ButtonGroup>

            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
              style={{ fontSize: 12, fontWeight: 600 }}
            >
              <RefreshIcon className={refreshing ? 'spinner' : ''} style={{ fontSize: 16, marginRight: 4 }} />
              Refresh
            </Button>

            <Button
              variant="outline-secondary"
              size="sm"
              onClick={toggleTheme}
              style={{ fontSize: 12, fontWeight: 600 }}
            >
              {isDark ? <LightModeIcon style={{ fontSize: 16 }} /> : <DarkModeIcon style={{ fontSize: 16 }} />}
            </Button>
          </div>
        </div>

        <Row className="g-3 mb-4">
          <Col lg={3} md={6}>
            <KPICard
              icon={AttachMoneyIcon}
              label="Total Revenue"
              value={formatCurrency(stats.totalRevenue)}
              subtitle={`${stats.totalJobs} orders`}
              trend="+12%"
              trendUp={true}
              loading={loading}
              colors={colors}
            />
          </Col>
          <Col lg={3} md={6}>
            <KPICard
              icon={InventoryIcon}
              label="Active Jobs"
              value={stats.activeJobs}
              subtitle={`${stats.totalJobs} total`}
              trend="+3"
              trendUp={true}
              loading={loading}
              colors={colors}
            />
          </Col>
          <Col lg={3} md={6}>
            <KPICard
              icon={SpeedIcon}
              label="Capacity"
              value={`${stats.avgUtilization.toFixed(0)}%`}
              subtitle={`${stats.runningMachines}/${stats.totalMachines} running`}
              trend="+5%"
              trendUp={true}
              loading={loading}
              colors={colors}
            />
          </Col>
          <Col lg={3} md={6}>
            <KPICard
              icon={PercentIcon}
              label="Profit Margin"
              value={formatPercent(stats.avgMargin)}
              subtitle={formatCurrency(stats.totalProfit)}
              trend="-2%"
              trendUp={false}
              loading={loading}
              colors={colors}
            />
          </Col>
        </Row>


        <Row className="g-3 mb-4">

          <Col lg={4}>
            <Card style={{ 
              background: colors.cardBg, 
              border: `1px solid ${colors.border}`, 
              borderRadius: 12, 
              height: '100%' 
            }}>
              <Card.Body className="p-3">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <AssessmentIcon style={{ fontSize: 20, color: colors.primary }} />
                  <div style={{ fontSize: 15, fontWeight: 700, color: colors.textPrimary }}>
                    Orders by Status
                  </div>
                </div>

                {loading ? (
                  <div style={{ textAlign: 'center', padding: 40 }}>
                    <LoopIcon className="spinner" style={{ fontSize: 32, color: colors.textMuted }} />
                  </div>
                ) : ordersByStatus.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie
                        data={ordersByStatus}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {ordersByStatus.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          background: colors.cardBg, 
                          border: `1px solid ${colors.border}`, 
                          borderRadius: 8 
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ textAlign: 'center', padding: 40, color: colors.textMuted }}>
                    No data available
                  </div>
                )}

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
                  {ordersByStatus.map(status => (
                    <div key={status.name} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
                      <div style={{ width: 10, height: 10, borderRadius: 2, background: status.color }} />
                      <span style={{ color: colors.textSecondary, fontWeight: 600 }}>
                        {status.name}: {status.value}
                      </span>
                    </div>
                  ))}
                </div>
              </Card.Body>
            </Card>
          </Col>


          <Col lg={8}>
            <Card style={{ 
              background: colors.cardBg, 
              border: `1px solid ${colors.border}`, 
              borderRadius: 12, 
              height: '100%' 
            }}>
              <Card.Body className="p-3">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <FactoryIcon style={{ fontSize: 20, color: colors.primary }} />
                  <div style={{ fontSize: 15, fontWeight: 700, color: colors.textPrimary }}>
                    Department Performance
                  </div>
                </div>

                {loading ? (
                  <div style={{ textAlign: 'center', padding: 40 }}>
                    <LoopIcon className="spinner" style={{ fontSize: 32, color: colors.textMuted }} />
                  </div>
                ) : departmentPerformance.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={departmentPerformance}>
                      <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
                      <XAxis 
                        dataKey="name" 
                        tick={{ fill: colors.textSecondary, fontSize: 11 }}
                        stroke={colors.border}
                      />
                      <YAxis 
                        tick={{ fill: colors.textSecondary, fontSize: 11 }}
                        stroke={colors.border}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          background: colors.cardBg, 
                          border: `1px solid ${colors.border}`, 
                          borderRadius: 8,
                          fontSize: 12
                        }}
                        formatter={(value) => formatCurrency(value)}
                      />
                      <Bar dataKey="revenue" fill={colors.primary} radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ textAlign: 'center', padding: 40, color: colors.textMuted }}>
                    No data available
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>


        <Row className="g-3 mb-4">

          <Col lg={8}>
            <Card style={{ 
              background: colors.cardBg, 
              border: `1px solid ${colors.border}`, 
              borderRadius: 12 
            }}>
              <Card.Body className="p-3">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <FactoryIcon style={{ fontSize: 20, color: colors.primary }} />
                  <div style={{ fontSize: 15, fontWeight: 700, color: colors.textPrimary }}>
                    Department Status
                  </div>
                </div>

                {loading ? (
                  <div style={{ textAlign: 'center', padding: 40 }}>
                    <LoopIcon className="spinner" style={{ fontSize: 32, color: colors.textMuted }} />
                  </div>
                ) : departmentPerformance.length > 0 ? (
                  <Row className="g-2">
                    {departmentPerformance.map(dept => (
                      <Col key={dept.name} md={6} lg={4}>
                        <DepartmentCard dept={dept} colors={colors} />
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <div style={{ textAlign: 'center', padding: 40, color: colors.textMuted }}>
                    No departments found
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>


          <Col lg={4}>
            <Card style={{ 
              background: colors.cardBg, 
              border: `1px solid ${colors.border}`, 
              borderRadius: 12,
              height: '100%'
            }}>
              <Card.Body className="p-3">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div className="d-flex align-items-center gap-2">
                    <WarningAmberIcon style={{ fontSize: 20, color: colors.warning }} />
                    <div style={{ fontSize: 15, fontWeight: 700, color: colors.textPrimary }}>
                      Alerts & Notifications
                    </div>
                  </div>
                  <Badge bg="danger" pill style={{ fontSize: 10, fontWeight: 600 }}>
                    {alerts.length}
                  </Badge>
                </div>

                <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                  {alerts.length > 0 ? (
                    alerts.map((alert, idx) => (
                      <AlertItem key={idx} alert={alert} colors={colors} />
                    ))
                  ) : (
                    <div style={{ textAlign: 'center', padding: 40 }}>
                      <CheckCircleIcon style={{ fontSize: 48, color: colors.success, marginBottom: 12 }} />
                      <div style={{ fontSize: 13, color: colors.textSecondary }}>
                        All systems operational
                      </div>
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>


        <Row className="g-3 mb-4">

          <Col lg={4}>
            <Card style={{ 
              background: colors.cardBg, 
              border: `1px solid ${colors.border}`, 
              borderRadius: 12 
            }}>
              <Card.Body className="p-3">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div className="d-flex align-items-center gap-2">
                    <BuildIcon style={{ fontSize: 20, color: colors.primary }} />
                    <div style={{ fontSize: 15, fontWeight: 700, color: colors.textPrimary }}>
                      Machine Status
                    </div>
                  </div>
                  <Badge bg="secondary" style={{ fontSize: 10, fontWeight: 600 }}>
                    {stats.runningMachines}/{stats.totalMachines}
                  </Badge>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {loading ? (
                    <div style={{ textAlign: 'center', padding: 40 }}>
                      <LoopIcon className="spinner" style={{ fontSize: 32, color: colors.textMuted }} />
                    </div>
                  ) : machineStatusSummary.length > 0 ? (
                    machineStatusSummary.map(machine => (
                      <MachineStatusChip key={machine.id} machine={machine} colors={colors} />
                    ))
                  ) : (
                    <div style={{ textAlign: 'center', padding: 40, color: colors.textMuted, fontSize: 13 }}>
                      No machines found
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>


          <Col lg={8}>
            <Card style={{ 
              background: colors.cardBg, 
              border: `1px solid ${colors.border}`, 
              borderRadius: 12 
            }}>
              <Card.Body className="p-3">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <TimelineIcon style={{ fontSize: 20, color: colors.primary }} />
                  <div style={{ fontSize: 15, fontWeight: 700, color: colors.textPrimary }}>
                    Recent Orders
                  </div>
                </div>

                {loading ? (
                  <div style={{ textAlign: 'center', padding: 40 }}>
                    <LoopIcon className="spinner" style={{ fontSize: 32, color: colors.textMuted }} />
                  </div>
                ) : recentOrders.length > 0 ? (
                  <Table responsive hover size="sm" style={{ marginBottom: 0 }}>
                    <thead>
                      <tr style={{ borderBottom: `2px solid ${colors.border}` }}>
                        <th style={{ fontSize: 11, color: colors.textMuted, fontWeight: 600, textTransform: 'uppercase' }}>
                          Order
                        </th>
                        <th style={{ fontSize: 11, color: colors.textMuted, fontWeight: 600, textTransform: 'uppercase' }}>
                          Customer
                        </th>
                        <th style={{ fontSize: 11, color: colors.textMuted, fontWeight: 600, textTransform: 'uppercase' }}>
                          Status
                        </th>
                        <th style={{ fontSize: 11, color: colors.textMuted, fontWeight: 600, textTransform: 'uppercase' }}>
                          Due Date
                        </th>
                        <th style={{ fontSize: 11, color: colors.textMuted, fontWeight: 600, textTransform: 'uppercase', textAlign: 'right' }}>
                          Revenue
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentOrders.map((order, idx) => (
                        <tr key={idx} style={{ borderBottom: `1px solid ${colors.border}` }}>
                          <td style={{ fontSize: 12, color: colors.textPrimary, fontWeight: 600 }}>
                            {order.projectNumber}
                          </td>
                          <td style={{ fontSize: 12, color: colors.textSecondary }}>
                            {order.customerName}
                          </td>
                          <td>
                            <Badge 
                              bg="secondary" 
                              style={{ 
                                fontSize: 10, 
                                fontWeight: 600,
                                background: STATUS_COLORS[normalizeStatus(order.status)] 
                              }}
                            >
                              {normalizeStatus(order.status)}
                            </Badge>
                          </td>
                          <td style={{ fontSize: 11, color: colors.textSecondary }}>
                            {formatDate(order.plannedFinish)}
                          </td>
                          <td style={{ fontSize: 12, color: colors.textPrimary, fontWeight: 600, textAlign: 'right' }}>
                            {formatCurrency(order.cost.revenue || 0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <div style={{ textAlign: 'center', padding: 40, color: colors.textMuted, fontSize: 13 }}>
                    No recent orders
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>


        <Card style={{ 
          background: colors.cardBg, 
          border: `1px solid ${colors.border}`, 
          borderRadius: 12 
        }}>
          <Card.Body className="p-3">
            <div className="d-flex align-items-center gap-2 mb-3">
              <PlayArrowIcon style={{ fontSize: 20, color: colors.primary }} />
              <div style={{ fontSize: 15, fontWeight: 700, color: colors.textPrimary }}>
                Quick Actions
              </div>
            </div>

            <div className="d-flex flex-wrap gap-2">
              <Button variant="outline-primary" size="sm" style={{ fontSize: 13, fontWeight: 600 }}>
                <AddIcon style={{ fontSize: 16, marginRight: 4 }} />
                Schedule Job
              </Button>
              <Button variant="outline-primary" size="sm" style={{ fontSize: 13, fontWeight: 600 }}>
                <InventoryIcon style={{ fontSize: 16, marginRight: 4 }} />
                Add Order
              </Button>
              <Button variant="outline-primary" size="sm" style={{ fontSize: 13, fontWeight: 600 }}>
                <BuildIcon style={{ fontSize: 16, marginRight: 4 }} />
                Machine Setup
              </Button>
              <Button variant="outline-primary" size="sm" style={{ fontSize: 13, fontWeight: 600 }}>
                <AssessmentIcon style={{ fontSize: 16, marginRight: 4 }} />
                View Reports
              </Button>
              <Button variant="outline-primary" size="sm" style={{ fontSize: 13, fontWeight: 600 }}>
                <FileDownloadIcon style={{ fontSize: 16, marginRight: 4 }} />
                Export Data
              </Button>
            </div>
          </Card.Body>
        </Card>
      </Container>


      <style>{`
        .spinner {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        /* Scrollbar styling */
        ::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        ::-webkit-scrollbar-track {
          background: ${colors.surface};
          border-radius: 4px;
        }
        ::-webkit-scrollbar-thumb {
          background: ${colors.border};
          border-radius: 4px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: ${colors.textMuted};
        }
      `}</style>
    </div>
  );
};

export default EliteProductionDashboard;
